import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

type SaveEligibilityReportContextValue = {
  isEligibilityReportWrittenBackInPMS: boolean;
  isEligibilityReportWritebackError: boolean;
};

type SaveEligibilityReportUpdaterContextValue = {
  setIsEligibilityReportWrittenBackInPMS: Dispatch<SetStateAction<boolean>>;
  setIsEligibilityReportWritebackError: Dispatch<SetStateAction<boolean>>;
};

// context
export const SaveEligibilityReportContext = React.createContext<SaveEligibilityReportContextValue>({
  isEligibilityReportWrittenBackInPMS: false,
  isEligibilityReportWritebackError: false,
});

export const SaveEligibilityReportUpdaterContext = React.createContext<SaveEligibilityReportUpdaterContextValue>(
  {} as SaveEligibilityReportUpdaterContextValue
);

// provider
export const SaveEligibilityReportProvider = React.memo(({ children }: { children: React.ReactNode }) => {
  const [isEligibilityReportWrittenBackInPMS, setIsEligibilityReportWrittenBackInPMS] = useState<boolean>(false);
  const [isEligibilityReportWritebackError, setIsEligibilityReportWritebackError] = useState<boolean>(false);

  const saveEligibilityReportProviderValue = useMemo(() => {
    return {
      isEligibilityReportWrittenBackInPMS,
      isEligibilityReportWritebackError,
    };
  }, [isEligibilityReportWrittenBackInPMS, isEligibilityReportWritebackError]);

  const saveEligibilityReportUpdaterProviderValue = useMemo(() => {
    return {
      setIsEligibilityReportWrittenBackInPMS,
      setIsEligibilityReportWritebackError,
    };
  }, [setIsEligibilityReportWrittenBackInPMS, setIsEligibilityReportWritebackError]);

  return (
    <SaveEligibilityReportContext.Provider value={saveEligibilityReportProviderValue}>
      <SaveEligibilityReportUpdaterContext.Provider value={saveEligibilityReportUpdaterProviderValue}>
        {children}
      </SaveEligibilityReportUpdaterContext.Provider>
    </SaveEligibilityReportContext.Provider>
  );
});
SaveEligibilityReportProvider.displayName = 'SaveEligibilityReportProvider';

// hooks
export const useSaveEligibilityReport = () => React.useContext(SaveEligibilityReportContext);
export const useSaveEligibilityReportUpdater = () => {
  const saveEligibilityReportUpdater = React.useContext(SaveEligibilityReportUpdaterContext);

  const updateIsEligibilityReportWrittenbackInPMS = React.useCallback(
    (value: boolean | null) => {
      if (saveEligibilityReportUpdater?.setIsEligibilityReportWrittenBackInPMS)
        saveEligibilityReportUpdater?.setIsEligibilityReportWrittenBackInPMS(value ?? false);
    },
    [saveEligibilityReportUpdater]
  );

  const updateIsEligibilityReportWritebackError = React.useCallback(
    (value: boolean | null) => {
      if (saveEligibilityReportUpdater?.setIsEligibilityReportWritebackError)
        saveEligibilityReportUpdater?.setIsEligibilityReportWritebackError(value ?? false);
    },
    [saveEligibilityReportUpdater]
  );

  return { updateIsEligibilityReportWrittenbackInPMS, updateIsEligibilityReportWritebackError };
};
